import React, { useState, useRef, useEffect } from 'react';
import { Download, Info } from 'lucide-react';
import soloLogo from '../Imagenes/Solo logo.jpg';
import logoRocharamas from '../Imagenes/lOGO ROCHARAMAS.jpg';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, increment, onSnapshot, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const GeneradorDeGuia = () => {
  const [nailCount, setNailCount] = useState(250);
  const [diameter, setDiameter] = useState(50);
  const [paperSize, setPaperSize] = useState('letter');
  const [isGenerating, setIsGenerating] = useState(false);
  const [stats, setStats] = useState({ visits: 0, generated: 0 });
  const [visitorInfo, setVisitorInfo] = useState(null);
  const canvasRef = useRef(null);

  // Obtener o crear ID de visitante único
  const getVisitorId = () => {
    let id = localStorage.getItem('rocharamas_visitor_id');
    if (!id) {
      id = 'vis_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      localStorage.setItem('rocharamas_visitor_id', id);
    }
    return id;
  };

  // Cargar y actualizar contadores de Firebase
  useEffect(() => {
    const statsRef = doc(db, 'stats', 'global');
    const visitorId = getVisitorId();

    const initAndListen = async () => {
      try {
        // 1. Obtener ubicación (IP Geolocation)
        let locationData = { city: 'Unknown', country: 'Unknown', ip: 'Unknown' };
        try {
          const res = await fetch('https://ipapi.co/json/');
          const data = await res.json();
          locationData = {
            city: data.city,
            region: data.region,
            country: data.country_name,
            ip: data.ip,
            postal: data.postal
          };
          setVisitorInfo(locationData);
        } catch (e) {
          console.error("Error obteniendo ubicación:", e);
        }

        // 2. Registrar la visita detallada
        await addDoc(collection(db, 'detailed_visits'), {
          visitorId,
          timestamp: serverTimestamp(),
          ...locationData,
          userAgent: navigator.userAgent
        });

        // 3. Actualizar contador global (UI)
        const snap = await getDoc(statsRef);
        if (!snap.exists()) {
          await setDoc(statsRef, { visits: 0, generated: 0 });
        }
        await updateDoc(statsRef, { visits: increment(1) });

        // 4. Escuchar cambios globales para la UI
        return onSnapshot(statsRef, (doc) => {
          if (doc.exists()) {
            setStats(doc.data());
          }
        });
      } catch (error) {
        console.error("Error con Firebase:", error);
      }
    };

    const unsubscribe = initAndListen();
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // Valores de seguridad para evitar errores cuando el input está vacío durante la edición
  const safeNailCount = parseInt(nailCount) || 0;
  const safeDiameter = parseInt(diameter) || 0;
  const isMissingFields = !nailCount || !diameter;

  // Calcular posiciones de los clavos en el círculo
  const calculateNailPositions = () => {
    const positions = [];
    const radius = 150; // Radio para la previsualización
    const centerX = 200;
    const centerY = 200;

    for (let i = 0; i < safeNailCount; i++) {
      // Empezar desde la derecha (0°) e incrementar en sentido horario
      const angle = (i * 360 / safeNailCount) * (Math.PI / 180);
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      positions.push({ x, y, index: i });
    }

    return positions;
  };

  const positions = calculateNailPositions();

  // Generar PDF usando jsPDF (cargado desde CDN)
  const generatePDF = async () => {
    setIsGenerating(true);
    
    // Dar tiempo a que el estado se actualice en la UI
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      // Esperar a que jsPDF esté disponible (máximo 10 segundos)
      await new Promise((resolve, reject) => {
        if (window.jspdf) {
          resolve();
          return;
        }
        
        const timeout = setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error('No se pudo cargar la librería PDF. Por favor, revisa tu conexión a internet.'));
        }, 10000);

        const checkInterval = setInterval(() => {
          if (window.jspdf) {
            clearInterval(checkInterval);
            clearTimeout(timeout);
            resolve();
          }
        }, 100);
      });

      const { jsPDF } = window.jspdf;
      
      // Configuración de página
      const pageWidth = paperSize === 'letter' ? 215.9 : 210; // mm
      const pageHeight = paperSize === 'letter' ? 279.4 : 297; // mm
      
      // Convertir diámetro a mm
      const diameterMm = diameter * 10;
      const radiusMm = diameterMm / 2;
      
      // Calcular cuántas páginas se necesitan
      const pagesWide = Math.ceil(diameterMm / pageWidth);
      const pagesTall = Math.ceil(diameterMm / pageHeight);
      const totalPages = pagesWide * pagesTall;
      
      // Centro del círculo completo (en mm)
      const circleCenterX = (pagesWide * pageWidth) / 2;
      const circleCenterY = (pagesTall * pageHeight) / 2;
      
      // Calcular posiciones de los clavos
      const nailPositions = [];
      for (let i = 0; i < safeNailCount; i++) {
        const angle = (i * 360 / safeNailCount) * (Math.PI / 180);
        const x = circleCenterX + radiusMm * Math.cos(angle);
        const y = circleCenterY + radiusMm * Math.sin(angle);
        nailPositions.push({
          x,
          y,
          index: i,
          angle
        });
      }
      
      // Crear PDF
      const pdfDoc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: paperSize === 'letter' ? [215.9, 279.4] : 'a4'
      });
      
      let pageNum = 0;
      
      // Generar cada página
      for (let row = 0; row < pagesTall; row++) {
        for (let col = 0; col < pagesWide; col++) {
          // Ceder control al navegador cada página para no bloquear el hilo principal
          await new Promise(resolve => setTimeout(resolve, 0));

          if (pageNum > 0) {
            pdfDoc.addPage();
          }
          pageNum++;
          
          // Coordenadas de origen de esta página
          const pageOriginX = col * pageWidth;
          const pageOriginY = row * pageHeight;

          // Marca de agua sutil de fondo
          pdfDoc.saveGraphicsState();
          pdfDoc.setTextColor(240, 240, 240);
          pdfDoc.setFontSize(60);
          pdfDoc.setFont(undefined, 'bold');
          // Dibujar "Rocharamas" diagonalmente
          pdfDoc.text('Rocharamas', pageWidth / 2, pageHeight / 2, {
            angle: 45,
            align: 'center'
          });
          pdfDoc.setFontSize(30);
          pdfDoc.text('rocharamas.com', pageWidth / 2, pageHeight / 2 + 20, {
            angle: 45,
            align: 'center'
          });
          pdfDoc.restoreGraphicsState();
          
          // Información de la página
          pdfDoc.setTextColor(0, 0, 0); // Reset color
          pdfDoc.setFontSize(10);
          pdfDoc.setFont(undefined, 'bold');
          pdfDoc.text(`Página ${pageNum}/${totalPages} | Posición: Fila ${row + 1}/${pagesTall}, Columna ${col + 1}/${pagesWide}`, 5, 8);
          
          pdfDoc.setFontSize(8);
          pdfDoc.setFont(undefined, 'normal');
          pdfDoc.text(`Diámetro: ${safeDiameter}cm | Clavos: ${safeNailCount} | Numeración: 0-${safeNailCount - 1}`, 5, 13);
          
          // Branding adicional en el pie de página
          pdfDoc.setFontSize(7);
          pdfDoc.setTextColor(150, 150, 150);
          
          // Línea 1: Redes sociales (izquierda) y web (derecha)
          const footerY = pageHeight - 8;
          pdfDoc.text('IG: @rocharamas | TK: @rocharamas | Tel: 614-130-3441', 5, footerY);
          pdfDoc.text('rocharamas.com', pageWidth - 5, footerY, { align: 'right' });
          
          // Línea 2: Nota legal (centro)
          pdfDoc.text('Plantilla gratuita - ¡Síguenos para más arte!', pageWidth / 2, pageHeight - 4, { align: 'center' });
          
          pdfDoc.setTextColor(0, 0, 0); // Reset color nuevamente
          
          // Guías de alineación
          pdfDoc.setDrawColor(128, 128, 128);
          pdfDoc.setLineWidth(0.2);
          
          const guideLength = 7;
          
          // Guías verticales y horizontales cada 20mm
          for (let i = 0; i <= pageHeight; i += 20) {
            if (i > 20) { // Evitar el área de información
              // Izquierda
              pdfDoc.line(0, i, guideLength, i);
              // Derecha
              pdfDoc.line(pageWidth - guideLength, i, pageWidth, i);
            }
          }
          
          for (let i = 0; i <= pageWidth; i += 20) {
            // Abajo
            pdfDoc.line(i, 0, i, guideLength);
            // Arriba
            pdfDoc.line(i, pageHeight - guideLength, i, pageHeight);
          }
          
          // Dibujar sección del círculo
          pdfDoc.setDrawColor(180, 180, 180);
          pdfDoc.setLineWidth(0.3);
          
          const localCircleX = circleCenterX - pageOriginX;
          const localCircleY = circleCenterY - pageOriginY;
          
          // Dibujar el arco del círculo
          if (localCircleX + radiusMm > 0 && 
              localCircleX - radiusMm < pageWidth &&
              localCircleY + radiusMm > 0 && 
              localCircleY - radiusMm < pageHeight) {
            
            const segments = 360;
            for (let i = 0; i < segments; i++) {
              const angle1 = (i * 360 / segments) * (Math.PI / 180);
              const angle2 = ((i + 1) * 360 / segments) * (Math.PI / 180);
              
              const x1 = localCircleX + radiusMm * Math.cos(angle1);
              const y1 = localCircleY + radiusMm * Math.sin(angle1);
              const x2 = localCircleX + radiusMm * Math.cos(angle2);
              const y2 = localCircleY + radiusMm * Math.sin(angle2);
              
              if ((x1 >= 0 && x1 <= pageWidth && y1 >= 0 && y1 <= pageHeight) ||
                  (x2 >= 0 && x2 <= pageWidth && y2 >= 0 && y2 <= pageHeight)) {
                pdfDoc.line(x1, y1, x2, y2);
              }
            }
          }
          
          // Dibujar clavos
          pdfDoc.setDrawColor(0, 0, 0);
          pdfDoc.setFillColor(0, 0, 0);
          
          nailPositions.forEach(nail => {
            const localX = nail.x - pageOriginX;
            const localY = nail.y - pageOriginY;
            
            if (localX >= 0 && localX <= pageWidth && localY >= 0 && localY <= pageHeight) {
              // Punto del clavo (círculo de 2mm de diámetro)
              pdfDoc.circle(localX, localY, 1, 'F');
              
              // Número
              pdfDoc.setFontSize(7);
              const text = String(nail.index);
              const textWidth = pdfDoc.getTextWidth(text);
              
              // Posicionar el número
              const offset = 3;
              const textX = localX + offset * Math.cos(nail.angle) - textWidth / 2;
              const textY = localY + offset * Math.sin(nail.angle) + 1;
              
              pdfDoc.text(text, textX, textY);
            }
          });
          
          // Marcadores de esquina
          pdfDoc.setDrawColor(0, 0, 0);
          pdfDoc.setLineWidth(0.5);
          const cornerSize = 3;
          
          // Esquina inferior izquierda
          pdfDoc.line(0, 0, cornerSize, 0);
          pdfDoc.line(0, 0, 0, cornerSize);
          
          // Esquina inferior derecha
          pdfDoc.line(pageWidth - cornerSize, 0, pageWidth, 0);
          pdfDoc.line(pageWidth, 0, pageWidth, cornerSize);
          
          // Esquina superior izquierda
          pdfDoc.line(0, pageHeight - cornerSize, 0, pageHeight);
          pdfDoc.line(0, pageHeight, cornerSize, pageHeight);
          
          // Esquina superior derecha
          pdfDoc.line(pageWidth - cornerSize, pageHeight, pageWidth, pageHeight);
          pdfDoc.line(pageWidth, pageHeight - cornerSize, pageWidth, pageHeight);
        }
      }
      
      // Guardar PDF
      pdfDoc.save('Guía Rocharamas.pdf');
      
      // Incrementar contador global (UI)
      const statsRef = doc(db, 'stats', 'global');
      await updateDoc(statsRef, { generated: increment(1) });

      // Registrar generación detallada
      await addDoc(collection(db, 'detailed_generations'), {
        visitorId: getVisitorId(),
        timestamp: serverTimestamp(),
        nailCount: safeNailCount,
        diameter: safeDiameter,
        paperSize,
        ...(visitorInfo || {}) // Incluir ubicación si está disponible
      });
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Por favor recarga la página e intenta nuevamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a365d] to-[#2b6cb0] p-4 md:p-8 font-serif">
      <link 
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;600;700&family=Permanent+Marker&display=swap" 
        rel="stylesheet"
      />
      
      <div className="max-w-[1200px] mx-auto bg-white/98 rounded-[20px] shadow-[0_20px_60px_rgba(0,0,0,0.4)] overflow-hidden relative">
        {/* Banner de "De a Grapa" */}
        <div className="absolute top-8 -right-12 bg-[#3182ce] text-white py-1.5 w-[200px] rotate-45 font-bold text-[0.8rem] md:text-[0.9rem] shadow-md z-10 flex justify-center items-center text-center">
          ¡ES DE A GRAPA!
        </div>

        {/* Header */}
        <div className="bg-gradient-to-br from-[#2c5282] to-[#2b6cb0] py-8 md:py-12 px-6 md:px-10 text-white text-center flex flex-col items-center">
          <img 
            src={logoRocharamas} 
            alt="Rocharamas Logo" 
            className="max-w-[280px] md:max-w-[400px] w-full h-auto mb-2 invert brightness-200 mix-blend-screen"
          />
          <h1 className="hidden">Rocharamas</h1>
          <p className="text-[1.1rem] md:text-[1.4rem] mb-4 opacity-95 font-['Permanent_Marker'] tracking-wider">
            Generador de Plantillas Maestras
          </p>
          <div className="bg-white/20 inline-block py-2 px-4 md:px-6 rounded-full text-[0.9rem] md:text-[1rem] font-['Lato']">
            ¡Ándale, haz tu arte sin que te cueste un peso! 🇲🇽
          </div>
        </div>

        <div className="px-4 md:px-10 pb-6 md:pb-10 max-w-[1000px] mx-auto">
          {/* Banner Promocional (Nuevo) */}
          <div className="mb-6 bg-gradient-to-r from-[#2b6cb0] to-[#4299e1] rounded-2xl p-1 shadow-md transform hover:scale-[1.01] transition-transform duration-300">
            <div className="bg-white rounded-[14px] p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-[#2b6cb0] font-bold text-lg md:text-xl mb-1 flex items-center justify-center md:justify-start gap-2">
                  <span>✨</span> ¿Quieres un acabado profesional?
                </h3>
                <p className="text-slate-600 text-sm md:text-base leading-snug">
                  Nuestros kits incluyen materiales premium y técnica de precisión para que tu obra luzca impecable. 
                  <span className="hidden md:inline"> ¡O nosotros lo hacemos por ti!</span>
                </p>
              </div>
              <a 
                href="https://rocharamas.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#2b6cb0] text-white px-6 py-3 rounded-xl font-bold text-sm md:text-base hover:bg-[#1a365d] transition-all shadow-lg hover:shadow-[#2b6cb0]/30 flex items-center gap-2 whitespace-nowrap"
              >
                Ver Kits y Retratos 🎨
              </a>
            </div>
          </div>

          {/* Tarjeta Unificada de Trabajo */}
          <div className="bg-white rounded-[20px] shadow-sm border border-slate-200 overflow-hidden">
            {/* Área de Previsualización (Hero) */}
            <div className="bg-[#ebf8ff] py-4 px-1 md:p-8 flex flex-col items-center relative border-b border-[#bee3f8]">
               <h2 className="text-xl md:text-2xl mb-4 md:mb-6 text-[#2b6cb0] flex items-center gap-2 opacity-90 text-center">
                Así se verá tu plantilla
              </h2>

              <div className="relative drop-shadow-[0_10px_15px_rgba(43,108,176,0.2)] w-full max-w-[400px]">
                <svg
                  ref={canvasRef}
                  viewBox="0 0 400 400"
                  className="bg-white rounded-full block mx-auto w-full h-auto"
                >
                  {/* Círculo de fondo */}
                  <circle
                    cx="200"
                    cy="200"
                    r="150"
                    fill="none"
                    stroke="#bee3f8"
                    strokeWidth="2"
                  />

                  {/* Puntos de clavos */}
                  {positions.map((pos, i) => (
                    <g key={i}>
                      {/* Punto */}
                      <circle
                        cx={pos.x}
                        cy={pos.y}
                        r="3"
                        fill="#2b6cb0"
                      />
                      {/* Número - mostrar solo algunos para no saturar */}
                      {i % Math.max(1, Math.floor(nailCount / 20)) === 0 && (
                        <text
                          x={pos.x}
                          y={pos.y}
                          fontSize="10"
                          fill="#2d3748"
                          textAnchor="middle"
                          dy="-8"
                          className="font-['Lato'] font-semibold"
                        >
                          {pos.index}
                        </text>
                      )}
                    </g>
                  ))}

                  {/* Indicador de inicio - solo si hay clavos */}
                  {positions.length > 0 && (
                    <g>
                      <circle
                        cx={positions[0].x}
                        cy={positions[0].y}
                        r="6"
                        fill="none"
                        stroke="#3182ce"
                        strokeWidth="2"
                      />
                      <text
                        x={positions[0].x + 15}
                        y={positions[0].y}
                        fontSize="12"
                        fill="#3182ce"
                        className="font-['Lato'] font-bold"
                      >
                        Inicio (0)
                      </text>
                    </g>
                  )}
                </svg>
              </div>

              {/* Rango de Numeración (Único dato informativo que queda aquí) */}
              <div className="mt-4 text-[#2b6cb0] font-bold text-sm bg-white/50 px-4 py-1 rounded-full border border-[#bee3f8]">
                Numeración: 0 - {safeNailCount > 0 ? safeNailCount - 1 : 0}
              </div>
            </div>

            {/* Barra de Controles */}
            <div className="p-4 md:p-8 bg-white">
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-end">
                
                {/* Control: Clavos (Display + Input) */}
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 font-['Lato'] flex justify-between items-center">
                    <span>Cantidad de Clavos</span>
                    {!nailCount && <span className="text-red-500 text-xs animate-pulse">¡Falta!</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={nailCount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNailCount(val === '' ? '' : parseInt(val));
                      }}
                      min="10"
                      max="500"
                      placeholder="Ej: 250"
                      className={`w-full p-3 text-xl border-2 rounded-lg outline-none font-lato transition-all font-bold focus:border-[#2b6cb0] ${
                        !nailCount ? 'border-red-200 bg-red-50' : 'border-slate-200 text-[#2b6cb0]'
                      }`}
                      onBlur={(e) => {
                        let val = parseInt(e.target.value);
                        if (isNaN(val) || val < 10) val = 10;
                        if (val > 500) val = 500;
                        setNailCount(val);
                      }}
                    />
                  </div>
                </div>

                {/* Control: Diámetro (Display + Input) */}
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 font-['Lato'] flex justify-between items-center">
                    <span>Diámetro (cm)</span>
                    {!diameter && <span className="text-red-500 text-xs animate-pulse">¡Falta!</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={diameter}
                      onChange={(e) => {
                        const val = e.target.value;
                        setDiameter(val === '' ? '' : parseInt(val));
                      }}
                      min="20"
                      max="200"
                      placeholder="Ej: 50"
                      className={`w-full p-3 text-xl border-2 rounded-lg outline-none font-lato transition-all font-bold focus:border-[#2b6cb0] ${
                        !diameter ? 'border-red-200 bg-red-50' : 'border-slate-200 text-[#2b6cb0]'
                      }`}
                      onBlur={(e) => {
                        let val = parseInt(e.target.value);
                        if (isNaN(val) || val < 20) val = 20;
                        if (val > 200) val = 200;
                        setDiameter(val);
                      }}
                    />
                  </div>
                </div>

                {/* Control: Papel */}
                <div>
                  <label className="block mb-2 font-semibold text-slate-700 font-['Lato'] flex justify-between items-center">
                    <span>Tamaño de Papel</span>
                    <span className="text-[10px] text-slate-400 font-normal italic">Más común: Carta</span>
                  </label>
                  <div className="flex bg-slate-50 p-1 rounded-lg border border-slate-200">
                    <button
                      onClick={() => setPaperSize('letter')}
                      className={`flex-1 py-2 px-2 text-xs md:text-sm font-semibold rounded-md transition-all flex flex-col items-center ${
                        paperSize === 'letter' ? 'bg-white text-[#2b6cb0] shadow-sm' : 'bg-transparent text-slate-400'
                      }`}
                    >
                      <span>Carta</span>
                      <span className="text-[9px] font-normal opacity-70">21.6 x 27.9 cm</span>
                    </button>
                    <button
                      onClick={() => setPaperSize('a4')}
                      className={`flex-1 py-2 px-2 text-xs md:text-sm font-semibold rounded-md transition-all flex flex-col items-center ${
                        paperSize === 'a4' ? 'bg-white text-[#2b6cb0] shadow-sm' : 'bg-transparent text-slate-400'
                      }`}
                    >
                      <span>A4</span>
                      <span className="text-[9px] font-normal opacity-70">21.0 x 29.7 cm</span>
                    </button>
                  </div>
                </div>

                {/* Botón Acción */}
                <div>
                  <button
                    onClick={generatePDF}
                    disabled={isGenerating || isMissingFields}
                    className={`w-full p-3.5 text-lg font-bold text-white rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg ${
                      isGenerating || isMissingFields
                        ? 'bg-gradient-to-br from-slate-400 to-slate-500 cursor-not-allowed shadow-none'
                        : 'bg-gradient-to-br from-[#4299e1] to-[#2b6cb0] shadow-[#2b6cb0]/40 hover:shadow-[#2b6cb0]/50 hover:-translate-y-0.5 active:translate-y-0'
                    }`}
                  >
                    <Download size={22} />
                    {isGenerating ? 'Generando...' : isMissingFields ? 'Faltan campos' : 'Descargar PDF'}
                  </button>
                </div>

               </div>
               
               {/* Redes Sociales (Compacto) */}
               <div className="mt-8 pt-4 border-t border-dashed border-slate-200 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-[0.9rem] text-slate-400 flex-wrap font-['Lato']">
                  <a href="https://www.instagram.com/rocharamas" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#2b6cb0] transition-colors">
                    📷 <strong>@rocharamas</strong>
                  </a>
                  <a href="https://www.tiktok.com/@rocharamas" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#2b6cb0] transition-colors">
                    🎵 <strong>@rocharamas</strong>
                  </a>
                  <a href="https://rocharamas.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#2b6cb0] transition-colors">
                    🌐 <strong>rocharamas.com</strong>
                  </a>
                  <a href="tel:6141303441" className="flex items-center gap-1 hover:text-[#2b6cb0] transition-colors">
                    📱 <strong>614-130-3441</strong>
                  </a>
               </div>
            </div>
          </div>
        </div>

        {/* Footer con instrucciones y amor mexicano */}
        <div className="bg-[#ebf8ff] p-6 md:p-10 border-t-2 border-[#bee3f8]">
          <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-8">
            <div>
              <h3 className="text-xl md:text-2xl mb-4 text-[#2b6cb0] font-['Permanent_Marker'] text-center md:text-left">
                Pasos para que te quede de 10:
              </h3>
              <ol className="m-0 pl-6 text-slate-600 leading-relaxed font-['Lato'] list-decimal text-sm md:text-base">
                <li>Ponle cuántos clavos quieres (no te pases, ¡luego cansa!).</li>
                <li>Ajusta el tamaño (el diámetro de tu círculo).</li>
                <li>Pícale a <strong>"Descargar PDF"</strong> y espérate a que salga.</li>
                <li>Imprime, arma el rompecabezas con las guías y ¡pum!, ya tienes tu plantilla.</li>
              </ol>
            </div>
            
            <div className="bg-white p-6 rounded-[15px] border-2 border-dashed border-[#4299e1] text-center">
              <div className="text-[2rem] md:text-[2.5rem] mb-2">🇲🇽</div>
              <h4 className="mb-2 text-slate-800 font-['Permanent_Marker']">
                Hecho en México con harto amor
              </h4>
              <p className="text-[0.9rem] text-slate-500 mb-4">
                Esta herramienta es <strong>100% gratuita</strong> porque en Rocharamas somos cuates.
              </p>
              <p className="text-[1rem] text-[#2b6cb0] font-bold mb-4">
                ¡Muchas gracias por seguirnos! <br/>
                No seas gacho y comparte tu arte.
              </p>
              
              <div className="mt-6 pt-4 border-t border-dashed border-[#bee3f8] text-[0.9rem] text-slate-600 flex justify-center gap-6 flex-wrap font-['Lato']">
                <a href="https://www.instagram.com/rocharamas" target="_blank" rel="noopener noreferrer" className="hover:text-[#2b6cb0] transition-colors">📷 rocharamas</a>
                <a href="https://www.tiktok.com/@rocharamas" target="_blank" rel="noopener noreferrer" className="hover:text-[#2b6cb0] transition-colors">🎵 rocharamas</a>
                <a href="https://rocharamas.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#2b6cb0] transition-colors">🌐 rocharamas.com</a>
                <a href="tel:6141303441" className="hover:text-[#2b6cb0] transition-colors">📱 614-130-3441</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="text-center mt-8 text-white text-[0.9rem] font-['Lato'] flex flex-col items-center gap-4 mb-8">
        <img 
          src={soloLogo} 
          alt="Logo" 
          className="w-[60px] h-auto grayscale-[100%] contrast-[200%] invert brightness-[150%] mix-blend-screen"
        />
        
        {/* Contadores de Firebase */}
        <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg flex gap-6 text-[0.8rem] opacity-90 border border-white/10">
          <div>👀 <span className="font-bold">{stats.visits.toLocaleString()}</span> visitas</div>
          <div className="w-[1px] bg-white/20"></div>
          <div>🎨 <span className="font-bold">{stats.generated.toLocaleString()}</span> plantillas creadas</div>
        </div>

        <div className="opacity-80">
          © {new Date().getFullYear()} Rocharamas - Creado para la comunidad artística.
        </div>
      </div>

      {/* Instrucciones finales */}
    </div>
  );
};

export default GeneradorDeGuia;
